import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { InvitationPreview, User } from 'shared'
import { Alert, Button, Field, FullPageSpinner, Input } from '../components/ui'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../utils/api'
import { toErrorMessage } from '../lib/errors'
import Logo from '../components/ui/Logo'

export default function InviteAccept() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()
  const { setSession } = useAuth()

  const [invitation, setInvitation] = useState<InvitationPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ data: InvitationPreview }>(`/api/invitations/${token}`)
      .then((response) => {
        setInvitation(response.data)
        setName(response.data.name ?? '')
      })
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
      .finally(() => setLoading(false))
  }, [token])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      const response = await api.post<{
        token: string
        user: User
        workspace_id: string
      }>(`/api/invitations/${token}/accept`, { password, name })

      setSession(response.token, response.user)
      navigate(`/w/${response.workspace_id}`, { replace: true })
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
      setSubmitting(false)
    }
  }

  if (loading) return <FullPageSpinner />

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 dark:bg-gray-950">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Logo />
        </div>

        {!invitation ? (
          <div className="rounded-lg bg-white p-8 text-center shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {t('invite.invalidTitle')}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {!error || error === 'error' ? t('invite.invalidDescription') : error}
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              {t('auth.login')}
            </Link>
          </div>
        ) : (
          <div className="rounded-lg bg-white p-8 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {t('invite.title', { workspace: invitation.workspace_name })}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {invitation.requires_login
                ? t('invite.existingAccount', { email: invitation.email })
                : t('invite.newAccount', { email: invitation.email })}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {!invitation.requires_login && (
                <Field label={t('auth.name')} required>
                  <Input
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </Field>
              )}

              <Field
                label={t('auth.password')}
                required
                hint={invitation.requires_login ? undefined : t('invite.passwordHelp')}
              >
                <Input
                  type="password"
                  required
                  minLength={8}
                  autoComplete={invitation.requires_login ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              {error && <Alert>{error === 'error' ? t('errors.generic') : error}</Alert>}

              <Button type="submit" className="w-full" loading={submitting}>
                {t('invite.accept')}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
